// Reglas de LINEUPS de CORAMPROF (ingreso, sustitución y presencia).
// Movidas desde EventINoOUT.jsx de `develop`; cada validación devuelve
// { ok: true } o { ok: false, message }, y el componente muestra el mensaje.
// Único cambio de lógica (aprobado): el portero se identifica por
// `positioncode === 1`; develop usaba `occupationcode === 1`.

// countMiddle no se lee, pero su rama en la cadena else-if evita que 35–39 se cuente como countYoung.
/* eslint-disable no-unused-vars */

import { maxStatus, minStatus } from "./composition";

const OK = { ok: true };
const fail = (message) => ({ ok: false, message });

// Banda de carnet que se muestra en el tablero del delegado: el portero va aparte.
const bandOf = (player) => {
  if (player.positioncode === 1) return "goalkeeper";
  if (player.edad >= 50) return "too_old";
  if (player.edad >= 45) return "old_middle";
  if (player.edad >= 40) return "old";
  if (player.edad >= 35) return "middle";
  if (player.edad >= 32) return "young";
  return null;
};

const coramprofRules = {
  // Texto del interruptor que marca presencia sin ingresar al campo.
  presenceLabel: "Marcar ingreso al partido",

  // Popup de ingreso sin etiqueta; PlayerRowBig solo marca al portero.
  playerTag: () => "",
  playerRowTag: (player) => (player.positioncode === 1 ? "(G)" : ""),

  // Tipo de jugador que muestra el tablero del delegado: solo se marca al portero.
  playerTypeLabel: (player) => (player.positioncode === 1 ? "Arquero" : ""),

  // Panel de composición del tablero del delegado: cantidad por carnet, carnets
  // en alerta y estado de cada regla de campo. Las reglas cuentan igual que
  // validateEntry/validateSubstitution (el portero cuenta por edad, salvo en ROJO).
  composition: (inField) => {
    const bands = {
      goalkeeper: 0,
      young: 0,
      middle: 0,
      old: 0,
      old_middle: 0,
      too_old: 0,
    };
    let tooOld = 0;
    let middleOld = 0;
    let old = 0;
    let young = 0;
    let goal = 0;
    let under40 = 0;
    inField.forEach((p) => {
      const band = bandOf(p);
      if (band) bands[band]++;

      if (p.edad >= 50) tooOld++;
      else if (p.edad >= 45) middleOld++;
      else if (p.edad >= 40) old++;
      else if (p.edad >= 32 && p.edad < 35 && p.positioncode !== 1) young++;

      if (p.edad < 40) under40++;
      if (p.positioncode === 1) goal++;
    });

    const n = inField.length;
    const over40 = old + middleOld + tooOld;
    const elders = tooOld >= 2 || (tooOld >= 1 && middleOld >= 1);

    return {
      bands,
      warn: {
        goalkeeper: goal > 1,
        young: young > 1,
        too_old: n >= 10 && !elders,
      },
      rules: [
        {
          name: "MARRÓN + GRIS o 2 MARRONES",
          value: elders ? "Cumple" : "Falta",
          status: elders ? "ok" : n >= 10 ? "bad" : "pending",
        },
        { name: "40+", value: `${over40}/6 mín.`, status: minStatus(over40, 6, n, 11) },
        { name: "Menores de 40", value: `${under40}/5 máx.`, status: maxStatus(under40, 5) },
        { name: "Arquero", value: `${goal}/1 máx.`, status: maxStatus(goal, 1) },
        { name: "Rojos", value: `${young}/1 máx.`, status: maxStatus(young, 1) },
      ],
    };
  },

  // No muestra el mensaje de sanción activa con fechas restantes.
  showSanctionMessage: false,

  // Una sanción activa se trata como tarjeta por pagar: se advierte y deja confirmar.
  blockActiveSanction: false,

  // Al confirmar el aviso de regla (ingreso o sustitución), se vuelven a revisar
  // las tarjetas antes de registrar.
  blockSanctionedOnConfirm: true,

  // Un jugador ya no puede volver a cambiarse si tuvo carnet young o más de una aparición.
  canSubstitute: ({ dataArr, usercode }) => {
    const userElements = dataArr.filter(
      (item) => item.usercode === usercode && item.eventcode !== 15
    );
    const eventos8Count = userElements.filter(
      (item) =>
        item.eventcode === 25 || item.eventcode === 8 || item.eventcode === 22
    ).length;
    const youngPresent = userElements.some((item) => item.media === "young");
    return !(youngPresent || eventos8Count > 1);
  },

  // Ingreso al campo. La presencia (isAttendance) pasa por las mismas reglas de edad.
  validateEntry: ({ inField, userIn }) => {
    let countTooOld = 0; // >=50  MARRÓN
    let countMiddleOld = 0; // 45–49 GRIS
    let countOld = 0; // 40–44
    let countMiddle = 0; // 35–39
    let countYoung = 0; // <40 ROJO
    let countGoal = 0;
    let countUnder40 = 0;

    // 1. CONTAR JUGADORES EN CANCHA (el portero sí cuenta para el tope de menores de 40,
    // solo queda fuera del cupo de carnet ROJO y de la exigencia de ser GRIS/MARRÓN)
    for (const p of inField) {
      if (p.edad >= 50) countTooOld++;
      else if (p.edad >= 45) countMiddleOld++;
      else if (p.edad >= 40) countOld++;
      else if (p.edad >= 35) countMiddle++;
      else if (p.edad >= 32 && p.positioncode !== 1) countYoung++;

      if (p.edad < 40) countUnder40++;
      if (p.positioncode === 1) countGoal++;
    }

    // 2. SIMULAR INGRESO DEL NUEVO JUGADOR (misma excepción: solo ROJO y la exigencia de edad quedan fuera)
    if (userIn.edad >= 50) countTooOld++;
    else if (userIn.edad >= 45) countMiddleOld++;
    else if (userIn.edad >= 40) countOld++;
    else if (userIn.edad >= 35) countMiddle++;
    else if (userIn.edad >= 32 && userIn.positioncode !== 1) countYoung++;

    if (userIn.edad < 40) countUnder40++;
    if (userIn.positioncode === 1) countGoal++;

    const total = inField.length;

    // 3. VALIDACIONES BÁSICAS
    if (countYoung > 1) {
      return fail("Solo se permite 1 carnet ROJO.");
    }

    if (countGoal > 1) {
      return fail("Solo se permite 1 portero.");
    }

    if (countUnder40 > 5) {
      return fail("Ya hay 5 menores de 40. Solo pueden ingresar mayores.");
    }

    // 4. REGLA EDAD MAYORES
    const cumpleReglaEdad =
      countTooOld >= 2 || (countTooOld >= 1 && countMiddleOld >= 1);

    // 5. BLOQUEO ENTRADAS FINALES (el portero no está sujeto a esta regla de edad)
    if (total === 9 && !cumpleReglaEdad && userIn.positioncode !== 1) {
      if (userIn.edad < 45) {
        return fail(
          "Con 9 jugadores y regla no cumplida, el ingreso debe ser CARNET MARRÓN (50+) o GRIS (45+)."
        );
      }
    }

    if (total === 10 && !cumpleReglaEdad && userIn.positioncode !== 1) {
      if (userIn.edad < 50) {
        return fail(
          "Con 10 jugadores y no cumplir la regla de los mayores, el ingreso debe ser CARNET MARRÓN (50+)."
        );
      }
    }

    if (total === 11 && !cumpleReglaEdad) {
      return fail(
        "No se cumple la regla: obligatorio MARRÓN + GRIS o 2 MARRONES."
      );
    }

    // 6. VALIDACIÓN FINAL
    if (total === 11) {
      const totalMayores = countOld + countMiddleOld + countTooOld;

      if (totalMayores < 6) {
        return fail("Debe haber al menos 6 mayores de 40.");
      }
    }

    return OK;
  },

  // Sustitución: sale userOut, entra userIn.
  validateSubstitution: ({ inField, userOut, userIn }) => {
    let countTooOld = 0; // >=50  MARRÓN
    let countMiddleOld = 0; // 45–49 GRIS
    let countOld = 0; // 40–44
    let countMiddle = 0; // 35–39
    let countYoung = 0; // <40 ROJO
    let countGoal = 0;
    let countUnder40 = 0;

    // 1. CONTAR CANCHA (SIMULANDO SALIDA); el portero sí cuenta para el tope de menores de
    // 40, solo queda fuera del cupo de carnet ROJO y de la exigencia de ser GRIS/MARRÓN
    for (const p of inField) {
      if (p.usercode === userOut.usercode) continue;

      if (p.edad >= 50) countTooOld++;
      else if (p.edad >= 45) countMiddleOld++;
      else if (p.edad >= 40) countOld++;
      else if (p.edad >= 35) countMiddle++;
      else if (p.edad >= 32 && p.positioncode !== 1) countYoung++;

      if (p.edad < 40) countUnder40++;
      if (p.positioncode === 1) countGoal++;
    }

    // 2. SIMULAR INGRESO; misma excepción: solo ROJO y la exigencia de edad quedan fuera
    if (userIn.edad >= 50) countTooOld++;
    else if (userIn.edad >= 45) countMiddleOld++;
    else if (userIn.edad >= 40) countOld++;
    else if (userIn.edad >= 35) countMiddle++;
    else if (userIn.edad >= 32 && userIn.positioncode !== 1) countYoung++;

    if (userIn.edad < 40) countUnder40++;
    if (userIn.positioncode === 1) countGoal++;

    const total = inField.length; // se mantiene igual en cambios

    // 3. VALIDACIONES BÁSICAS
    if (countYoung > 1) {
      return fail("Solo se permite 1 carnet ROJO.");
    }

    if (countGoal > 1) {
      return fail("Solo se permite 1 portero.");
    }

    if (countUnder40 > 5) {
      return fail("Ya hay 5 menores de 40. Solo pueden quedar mayores.");
    }

    // 4. REGLA DE MAYORES
    const cumpleReglaEdad =
      countTooOld >= 2 || (countTooOld >= 1 && countMiddleOld >= 1);

    // 5. BLOQUEOS SEGÚN MOMENTO (el portero no está sujeto a esta regla de edad)
    if (total === 9 && !cumpleReglaEdad && userIn.positioncode !== 1) {
      if (userIn.edad < 45) {
        return fail(
          "Con 9 jugadores y regla no cumplida, el cambio debe dejar GRIS (45+) o MARRÓN (50+)."
        );
      }
    }

    if (total === 10 && !cumpleReglaEdad && userIn.positioncode !== 1) {
      if (userIn.edad < 50) {
        return fail(
          "Con 10 jugadores y regla no cumplida, el cambio debe dejar MARRÓN (50+)."
        );
      }
    }

    if (total === 11 && !cumpleReglaEdad) {
      return fail(
        "Cambio no permitido: obligatorio MARRÓN + GRIS o 2 MARRONES."
      );
    }

    // 6. VALIDACIÓN FINAL
    if (total === 11) {
      const totalMayores = countOld + countMiddleOld + countTooOld;

      if (totalMayores < 6) {
        return fail("Debe haber al menos 6 mayores de 40.");
      }
    }

    return OK;
  },
};

export default coramprofRules;
